import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { MOCK_ENABLED, useAlerts, useDeadLetters, useLogout, useMe } from "@/api";
import { Badge, Button } from "@/ui";
import { FEATURE_ROUTES } from "./features";
import { ThemeToggle } from "./ThemeToggle";
import "./app.css";

interface NavItem {
  to: string;
  label: string;
  end?: boolean;
  count?: number;
  tone?: "danger" | "warning";
}

export function Layout() {
  const me = useMe();
  const logout = useLogout();
  const navigate = useNavigate();
  const alerts = useAlerts(true);
  const dead = useDeadLetters();

  const openAlerts = alerts.data?.total ?? 0;
  const deadCount = dead.data?.total ?? 0;

  const core: NavItem[] = [
    { to: "/", label: "Health", end: true, count: openAlerts, tone: "warning" },
    { to: "/sources", label: "Sources" },
    { to: "/mappings", label: "Mappings" },
    { to: "/settings", label: "Global settings" },
    { to: "/jobs", label: "Jobs" },
    { to: "/dead-letters", label: "Dead letters", count: deadCount, tone: "danger" },
    { to: "/accounts", label: "Account & token" },
  ];
  const nav = withFeatureLinks(core);

  const onLogout = () =>
    logout.mutate(undefined, { onSettled: () => navigate("/login", { replace: true }) });

  return (
    <div className="app-shell">
      <a className="app-skip" href="#main">
        Skip to content
      </a>
      <aside className="app-sidebar" aria-label="Primary">
        <div className="app-brand">
          <span className="app-brand__mark" aria-hidden="true" />
          <span className="app-brand__name">Relay</span>
          {MOCK_ENABLED ? (
            <Badge tone="info" variant="outline">
              mock
            </Badge>
          ) : null}
          <ThemeToggle />
        </div>
        <nav className="app-nav">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => (isActive ? "app-nav__link is-active" : "app-nav__link")}
            >
              <span>{item.label}</span>
              {item.count ? (
                <Badge tone={item.tone ?? "neutral"} aria-label={`${item.count} open`}>
                  {item.count}
                </Badge>
              ) : null}
            </NavLink>
          ))}
        </nav>
        <div className="app-sidebar__foot">
          <NavLink to="/kit" className="app-nav__link app-nav__link--quiet">
            UI kit
          </NavLink>
          <div className="app-user">
            <span className="app-user__name">{me.data?.username ?? "…"}</span>
            <Button variant="ghost" size="sm" onClick={onLogout} loading={logout.isPending}>
              Sign out
            </Button>
          </div>
        </div>
      </aside>
      <main id="main" className="app-main" tabIndex={-1}>
        <Outlet />
      </main>
    </div>
  );
}

/** Inserts the available Iteration 3 feature screens after their anchor nav item. */
function withFeatureLinks(core: NavItem[]): NavItem[] {
  return core.flatMap((item) => [
    item,
    ...FEATURE_ROUTES.filter((f) => f.after === item.to).map((f) => ({
      to: `/${f.path}`,
      label: f.navLabel,
    })),
  ]);
}
